import classNames from 'classnames';
import { Card } from '../../../../components/card/card';
import { Divider } from '../../../../components/divider/divider';
import { Field } from '../../../../components/field/field';
import { SelectForm } from '../../../../components/select-form/select-form';
import { TextArea } from '../../../../components/textarea/textarea';
import { ConfigBinaryProperty, ConfigProperty, ConfigSection } from '../config-property';
import {
  FEE_EXCEPTIONS_PER_CUSTOMER_OPTIONS,
  useConfigCreditCardPassThrough,
} from '../../hooks/use-config-credit-card-pass-through';

export const CreditCardPassThroughConfig = () => {
  const {
    // Values
    isBankACHEnabled,
    isCardFeeEnabled,
    useCustomDisclaimer,
    isBankFeeEnabled,
    customPercentageFee,
    customFixedFee,
    customDisclaimer,
    showDisclaimerSection,
    feeExceptionPerCustomer,
    defaultPassthroughPercentage,

    // Preview values
    previewFee,
    previewDisclaimer,

    // Validation
    errors,
    isSummaryValid,

    // Setters
    setIsBankACHEnabled,
    setIsCardFeeEnabled,
    setIsBankFeeEnabled,
    setUseCustomDisclaimer,
    setCustomDisclaimer,
    setCustomPercentageFee,
    setCustomFixedFee,
    setFeeExceptionPerCustomer,
  } = useConfigCreditCardPassThrough();

  return (
    <>
      {/* Bank/ACH */}
      <ConfigSection className="py-4" dataCy="bank-ach-section">
        <ConfigBinaryProperty
          dataCy="binary-property-enable-bank-ach-payments"
          property="Enable bank/ACH payments"
          description="Enable customers to set up and pay from verified bank accounts"
          value={isBankACHEnabled}
          onChange={(value) => setIsBankACHEnabled(value)}
        />

        {isBankACHEnabled && (
          <ConfigBinaryProperty
            dataCy="binary-property-bank-ach-fee"
            property="Charge customers Stripe's exact bank/ACH processing fees"
            description="Pass Stripe's exact processing fee for bank/ACH payments (0.8%, $5.00 cap) to customers."
            value={isBankFeeEnabled}
            onChange={(value) => setIsBankFeeEnabled(value)}
          />
        )}
      </ConfigSection>

      <ConfigSection title="Payment Fees" className="py-4" dataCy="payment-fees-section">
        {/* Summary */}
        <div className="max-w-lg">
          <Card className="!shadow-none !p-5 !rounded-md flex flex-col gap-4 w-full overflow-hidden">
            <span className="text-base text-quipli-black-800 font-bold">Credit/Debit Card Fee Summary</span>

            <Divider />

            <div className="flex flex-col gap-1">
              <span className="text-xs text-quipli-black-800 font-semibold">
                Total effective fee for credit/debit card payments:
              </span>
              <span
                className={classNames('text-base text-quipli-black-800 font-bold', {
                  'text-error-500': !isSummaryValid,
                })}
              >
                {previewFee}
              </span>
            </div>

            <div className="flex flex-col gap-1 overflow-hidden">
              <span className="text-xs text-quipli-black-800 font-semibold">
                Credit/Debit Card Fee Disclaimer text (shown on invoices and contracts):
              </span>
              <div
                className={classNames(
                  'text-md text-quipli-black-800 whitespace-pre-wrap break-words overflow-wrap-anywhere',
                  {
                    'text-error-500': !isSummaryValid,
                  },
                )}
                style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
              >
                {previewDisclaimer}
              </div>
            </div>
          </Card>
        </div>

        {/* Card */}
        <ConfigBinaryProperty
          dataCy="binary-property-card-fee"
          property="Charge customers Stripe's exact credit/debit card processing fees"
          description={`Pass Stripe's exact processing fee for credit/debit card payments (${defaultPassthroughPercentage}% + $0.30) to customers.`}
          value={isCardFeeEnabled}
          onChange={(value) => setIsCardFeeEnabled(value)}
        />

        <ConfigProperty
          dataCy="property-custom-percentage-card-fee"
          property="Custom percentage-based credit/debit card fee"
          description="Add a custom percentage fee on top of (or instead of) the Stripe fee for credit/debit card payments."
        >
          <Field
            type="number"
            symbol="%"
            format={false}
            value={customPercentageFee}
            onChange={(value) => setCustomPercentageFee(value)}
            invalid={!!errors.customPercentageFee}
            description={errors.customPercentageFee}
            descriptionType="text-xs"
            dataCy="custom-percentage-card-fee-field"
            maximumFractionDigits={2}
          />
        </ConfigProperty>

        <ConfigProperty
          dataCy="property-custom-fixed-card-fee"
          property="Custom fixed credit/debit card fee"
          description="Add a flat amount on top of (or instead of) the Stripe fee for credit/debit card payments."
        >
          <Field
            type="currency"
            value={customFixedFee}
            onChange={(value) => setCustomFixedFee(value)}
            invalid={!!errors.customFixedFee}
            description={errors.customFixedFee}
            descriptionType="text-xs"
            dataCy="custom-fixed-card-fee-field"
            maximumFractionDigits={2}
          />
        </ConfigProperty>

        {/* Disclaimer */}
        {showDisclaimerSection && (
          <>
            <ConfigBinaryProperty
              dataCy="binary-property-use-custom-card-fee-disclaimer-text"
              property="Use custom credit/debit card fee disclaimer text"
              description="Show your own message on invoices and contracts instead of the default credit/debit card fee disclaimer text."
              value={useCustomDisclaimer}
              onChange={(value) => setUseCustomDisclaimer(value)}
            />

            {useCustomDisclaimer && (
              <ConfigProperty
                dataCy="property-custom-card-fee-disclaimer-text"
                property="Custom credit/debit card fee disclaimer text"
                description="Customize the credit/debit card fee disclaimer text that appears on invoices and contracts. If left blank, no disclaimer text will appear.<br/><br/>You can include the following placeholders:<br/><br/>• <strong>{percentage}</strong> = Effective card-fee percentage<br/>• <strong>{fixed}</strong> = Effective card-fee fixed amount"
              >
                <TextArea
                  value={customDisclaimer}
                  onChange={({ target: { value } }) => setCustomDisclaimer(value)}
                  rows={3}
                  dataCy="custom-card-fee-disclaimer-text-field"
                  invalid={!!errors.customDisclaimer}
                  description={errors.customDisclaimer}
                  descriptionType="text-xs"
                />
              </ConfigProperty>
            )}
          </>
        )}

        {/* Fee exceptions per customer */}
        <ConfigProperty
          dataCy="property-fee-exceptions-per-customer"
          property="Enable fee exceptions per customer"
          description="Allow processing fee exemptions or custom rules for individual customers (applies to both card and bank/ACH payments)"
        >
          <SelectForm
            options={FEE_EXCEPTIONS_PER_CUSTOMER_OPTIONS}
            value={feeExceptionPerCustomer}
            onChange={(value) => value && setFeeExceptionPerCustomer(value)}
            dataCy="fee-exceptions-per-customer-select"
          />
        </ConfigProperty>
      </ConfigSection>
    </>
  );
};
